import type { LogEntry, LogQuery } from "@skaile/workspaces/types";
import { createTRPCUntypedClient, httpLink } from "@trpc/client";

import { superjsonLite } from "./superjson-transformer.ts";

export interface RemoteQueryResult {
  entries: LogEntry[];
  nextCursor?: string;
}

/**
 * Call the platform tRPC `workspace.queryLogs` procedure.
 *
 * Untyped client — the platform AppRouter is not exported as an installable
 * package across the workspaces / platform repo boundary. The shape of
 * the `queryLogs` procedure is stable and documented in
 * `platform/backend/libs/router-trpc/src/routes/workspace.route.ts`.
 *
 * The procedure expects:
 *   { projectId, sessionId?, query: LogQuery (without sessionId) }
 * and returns:
 *   { entries: LogEntry[], nextCursor?: string }
 */
export async function remoteQuery(args: {
  url: string;
  token?: string;
  projectId: string;
  sessionId: string;
  query: Omit<LogQuery, "sessionId">;
}): Promise<RemoteQueryResult> {
  const trpcUrl = normalizeTrpcUrl(args.url);
  // Untyped client — the platform AppRouter is not exported across the repo
  // boundary. Procedure path + input shape are validated at runtime by the
  // platform's Zod schema.
  const client = createTRPCUntypedClient({
    links: [
      httpLink({
        url: trpcUrl,
        transformer: superjsonLite,
        headers: () => (args.token ? { authorization: `Bearer ${args.token}` } : {}),
      }),
    ],
  });

  const result = (await client.query("workspace.queryLogs", {
    projectId: args.projectId,
    sessionId: args.sessionId,
    query: args.query,
  })) as RemoteQueryResult;
  // Platform returns DESC (newest first) to keep cursor pagination natural.
  // Reverse to chronological so remote output matches local mode and the
  // formatter reads top-to-bottom in time order.
  return { ...result, entries: result.entries.slice().reverse() };
}

function normalizeTrpcUrl(url: string): string {
  const trimmed = url.replace(/\/+$/, "");
  if (trimmed.endsWith("/trpc")) return trimmed;
  return `${trimmed}/trpc`;
}
